import { 
  Traveler, 
  CreateTravelerRequest, 
  UpdateTravelerRequest, 
  TravelerSummary,
  PersonType,
  PersonTypeInfo,
  LicenseClassInfo
} from '../types/traveler';
import { getToken } from '../utils/auth';

// Resolve API base: use absolute VITE_API_BASE when provided; otherwise current origin.
const _configured = (import.meta as any).env?.VITE_API_BASE as string | undefined;
const API_BASE_URL = _configured && /^https?:\/\//i.test(_configured) ? _configured : window.location.origin;

class TravelerApiService {
  private async fetchPublic(url: string, options: RequestInit = {}): Promise<Response> {
    const token = getToken();

    const headers = {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    };

    const response = await fetch(`${API_BASE_URL}/api${url}`, {
      ...options,
      headers,
    });

    if (!response.ok) {
      if (response.status === 401) {
        console.error('Unauthorized! Token may be invalid or expired');
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response;
  }

  async getAllTravelers(includeInactive: boolean = false, personType?: PersonType): Promise<Traveler[]> {
    let url = `/traveler?includeInactive=${includeInactive}`;
    if (personType) {
      url += `&personType=${personType}`;
    }
    
    const response = await this.fetchPublic(url);
    const data = await response.json();

    if (data.success) {
      return data.data;
    } else {
      throw new Error(data.message || 'Failed to fetch travelers');
    }
  }

  async getTravelerById(id: number): Promise<Traveler> {
    const url = `/traveler/${id}`;
    const response = await this.fetchPublic(url);
    const data = await response.json();

    if (data.success) {
      return data.data;
    } else {
      throw new Error(data.message || 'Failed to fetch traveler');
    }
  }

  async createTraveler(travelerData: CreateTravelerRequest): Promise<Traveler> {
    const url = `/traveler`;
    const response = await this.fetchPublic(url, {
      method: 'POST',
      body: JSON.stringify(travelerData),
    });
    const data = await response.json();

    if (data.success) {
      return data.data;
    } else {
      throw new Error(data.message || 'Failed to create traveler');
    }
  }

  async updateTraveler(id: number, travelerData: UpdateTravelerRequest): Promise<Traveler> {
    const url = `/traveler/${id}`;
    const response = await this.fetchPublic(url, {
      method: 'PUT',
      body: JSON.stringify(travelerData),
    });
    const data = await response.json();

    if (data.success) {
      return data.data;
    } else {
      throw new Error(data.message || 'Failed to update traveler');
    }
  }

  async deleteTraveler(id: number): Promise<void> {
    const url = `/traveler/${id}`;
    const response = await this.fetchPublic(url, {
      method: 'DELETE',
    });
    const data = await response.json();

    if (!data.success) {
      throw new Error(data.message || 'Failed to delete traveler');
    }
  }

  async toggleTravelerActive(id: number): Promise<void> {
    const url = `/traveler/${id}/toggle-active`;
    const response = await this.fetchPublic(url, {
      method: 'POST',
    });
    const data = await response.json();

    if (!data.success) {
      throw new Error(data.message || 'Failed to toggle traveler status');
    }
  }

  async getTravelerSummary(): Promise<TravelerSummary> {
    const url = `/traveler/summary`;
    const response = await this.fetchPublic(url);
    const data = await response.json();

    if (data.success) {
      return data.data;
    } else {
      throw new Error(data.message || 'Failed to fetch traveler summary');
    }
  }

  async getPersonTypes(): Promise<PersonTypeInfo[]> {
    const url = `/traveler/person-types`;
    const response = await this.fetchPublic(url);
    const data = await response.json();

    if (data.success) {
      return data.data;
    } else {
      throw new Error(data.message || 'Failed to fetch person types');
    }
  }

  async getLicenseClasses(): Promise<LicenseClassInfo[]> {
    const url = `/traveler/license-classes`;
    const response = await this.fetchPublic(url);
    const data = await response.json();

    if (data.success) {
      return data.data;
    } else {
      throw new Error(data.message || 'Failed to fetch license classes');
    }
  }

  // Convenience methods for specific person types
  async getDrivers(includeInactive: boolean = false): Promise<Traveler[]> {
    return this.getAllTravelers(includeInactive, 'Driver');
  }

  async getPlayers(includeInactive: boolean = false): Promise<Traveler[]> {
    return this.getAllTravelers(includeInactive, 'Player');
  }

  async getCoaches(includeInactive: boolean = false): Promise<Traveler[]> {
    return this.getAllTravelers(includeInactive, 'Coach');
  }

  async getStaff(includeInactive: boolean = false): Promise<Traveler[]> {
    return this.getAllTravelers(includeInactive, 'Staff');
  }

  // Validation methods
  async canDeleteTraveler(id: number): Promise<boolean> {
    try {
      // For now, assume all travelers can be deleted
      // This could be extended to check for dependencies
      return true;
    } catch (error) {
      console.error('Error checking delete status:', error);
      return false;
    }
  }
}

export const travelerApi = new TravelerApiService();
export const travelerApiService = new TravelerApiService();
